/**
 * Phase 8 · Cluster C-48 — Hardcoded agent safety list.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md §34.2.C
 *        (subsection "Autonomous-mode safety rules").
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §10.2 acceptance row 7
 *        ("send a rejection" with autonomous ON still shows Plan card).
 *
 * Tools on this list ALWAYS go through the Plan / Approve card,
 * regardless of:
 *   - the per-user `ggj_b2b_org_users.prefs.agent_autonomous` toggle,
 *   - whether the tool's typed result says `reversible: true`,
 *   - whether the step is a single call or part of a larger plan.
 *
 * Categories (per spec):
 *   1. External communication — `write.emails.send`,
 *      `write.outreach.sequence_start`. Any write P9 connector will be
 *      added here when it lands (it's out of scope for C-48 but this
 *      module stays the canonical place).
 *   2. Pipeline exits — `write.applications.reject`.
 *   3. Billable / regulated entity creation — `write.requisitions.create`,
 *      `write.requisitions.advance_stage`.
 *
 * Everything else defers to the runner's normal autonomous-mode logic:
 *   if autonomous_mode is ON and the single call is reversible, execute
 *   without a plan card (logging `approval_mode = 'auto'`). If the tool
 *   is on this list, that shortcut is skipped entirely.
 *
 * DO NOT make this list configurable via the database — the whole point
 * is that a recruiter cannot toggle off the "send email requires
 * approval" rule to race through a dozen rejections without thinking.
 * Audit would notice, but the damage would already be done.
 */

/**
 * Names of tools that ALWAYS require manual approval via the Plan card.
 * Strings match the `AgentTool.name` of the corresponding write tool in
 * `src/modules/agent/tools/writers.ts`. Keep this list in the order
 * from §34.2.C so it grep-matches the acceptance regex.
 */
export const AGENT_SAFETY_REQUIRE_APPROVAL: readonly string[] = Object.freeze([
  // External communication — recruiter-to-candidate email / sequences.
  'write.emails.send',
  'write.outreach.sequence_start',
  // Pipeline exits — permanent "no" decisions on a candidate.
  'write.applications.reject',
  // Billable / regulated entity creation + stage gates.
  'write.requisitions.create',
  'write.requisitions.advance_stage',
]);

/**
 * `true` when the given tool name is on the hardcoded safety list and
 * therefore must go through the Plan / Approve card regardless of the
 * user's autonomous-mode preference.
 *
 * The runner calls this from `agentRunnerService` when deciding whether
 * a single write tool can be auto-executed; the Plan card calls it as a
 * defence-in-depth check before executing an auto-approved step.
 */
export function requiresManualApproval(toolName: string): boolean {
  if (!toolName) return true; // fail closed on unknown input
  return AGENT_SAFETY_REQUIRE_APPROVAL.includes(toolName);
}

/**
 * Human-readable reason shown in the Plan card / Autonomous toggle
 * tooltip when the UI needs to explain *why* a step can't be
 * auto-approved. Keep these in sync with the three categories above.
 */
/**
 * Plain-English one-liner of the spec's autonomous-mode rules. Rendered
 * in the Autonomous toggle's tooltip so operators understand what ON
 * does and doesn't authorise. Keep wording tight — the list of tools
 * below it in the UI provides the enumeration.
 */
export const AGENT_SAFETY_RULES_SUMMARY =
  'Autonomous mode lets the agent run a single reversible write without a Plan card. Multi-step plans, and any tool below, still require your approval.';

export function safetyReason(toolName: string): string | null {
  if (toolName === 'write.emails.send' || toolName === 'write.outreach.sequence_start') {
    return 'sends an external email — always requires your approval';
  }
  if (toolName === 'write.applications.reject') {
    return 'removes a candidate from a pipeline — always requires your approval';
  }
  if (toolName === 'write.requisitions.create' || toolName === 'write.requisitions.advance_stage') {
    return 'changes a billable / regulated entity — always requires your approval';
  }
  return null;
}
